import { NgTemplateOutlet, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { EMPTY, Subject, catchError, distinctUntilChanged, finalize, forkJoin, interval, map, of, switchMap, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Loading } from '@components/ui/loading/loading';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { AssignmentProcessService as AssignmentProcessApiService } from '@6grain/masofa-monolith-api-client/common/api/assignmentProcess.service';
import { ProcessEventTriggerService as ProcessEventTriggerApiService } from '@6grain/masofa-monolith-api-client/common/api/processEventTrigger.service';
import { ProcessSubscriptionService as ProcessSubscriptionApiService } from '@6grain/masofa-monolith-api-client/common/api/processSubscription.service';
import { ProcessEventTriggerResponse } from '@6grain/masofa-monolith-api-client/common/model/processEventTriggerResponse';
import { ProcessSubscriptionResponse } from '@6grain/masofa-monolith-api-client/common/model/processSubscriptionResponse';
import { buildProcessCatalog } from '@app/pages/admin/orchestration-process-flows.util';
import { OrchestrationCatalogSearchComponent } from '@app/pages/admin/components/orchestration-catalog-search/orchestration-catalog-search';
import {
  catalogQueryMatches,
  eventCatalogCardMatchesQuery,
  processFlowMatchesQuery,
  standaloneProcessMatchesQuery,
} from '@app/pages/admin/orchestration-catalog-search.util';
import {
  PROCESS_GROUPS,
  groupEvents,
  groupProcessFlows,
  groupProcesses,
} from '@app/pages/admin/orchestration-process-groups.util';
import { QuartzCronLabelPipe } from '@app/pages/admin/orchestration-quartz-cron-label.pipe';
import { OrchestrationProcessEventsComponent } from '@app/pages/admin/components/orchestration-process-events/orchestration-process-events';
import { OrchestrationProcessActivityComponent } from '@app/pages/admin/components/orchestration-process-activity/orchestration-process-activity';
import { CopyableIdentifierComponent } from '@app/pages/admin/components/copyable-identifier/copyable-identifier';
import { CommandConfigLinksComponent } from '@app/pages/admin/orchestration-command-config-page/command-config-links';
import { coreProcessEvents } from '@app/pages/admin/orchestration-process-events.util';
import {
  attachProcessActivity,
  indexProcessActivity,
  PROCESS_ACTIVITY_POLL_MS,
} from '@app/pages/admin/orchestration-process-activity.util';
import { ProcessAssignmentActivityResponse } from '@6grain/masofa-monolith-api-client/common/model/processAssignmentActivityResponse';
import {
  ProcessEventCatalogItem,
  buildEventCatalog,
} from '@app/pages/admin/orchestration-events.util';
import { NOTIFY_PEOPLE_PATH, notifyPeopleQueryParams } from '@app/pages/admin/orchestration-notify-people.util';
import { operatorCatalogProcesses } from '@app/pages/admin/orchestration-process-keys.util';
import { processHasFailSubscriber } from '@app/pages/admin/orchestration-fail-subscriber.util';
import { NotificationRuleDto, UserNotificationsApiService } from '@app/services/user-notifications-api.service';

interface ProcessDefinition {
  key: string;
  displayName?: string;
  description?: string;
  version: number;
}

export type ProcessCatalogOrder = 'processes' | 'events';

@Component({
  selector: 'app-orchestration-processes-page',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    RouterModule,
    TranslateModule,
    Loading,
    SecondaryButton,
    QuartzCronLabelPipe,
    OrchestrationCatalogSearchComponent,
    OrchestrationProcessEventsComponent,
    OrchestrationProcessActivityComponent,
    CopyableIdentifierComponent,
    CommandConfigLinksComponent,
  ],
  templateUrl: './orchestration-processes-page.html',
  styleUrl: './orchestration-processes-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe],
})
export class OrchestrationProcessesPageComponent implements OnInit, OnDestroy {
  private readonly service = inject(AssignmentProcessApiService);
  private readonly triggersApi = inject(ProcessEventTriggerApiService);
  private readonly subscriptionsApi = inject(ProcessSubscriptionApiService);
  private readonly notificationsApi = inject(UserNotificationsApiService);
  private readonly datePipe = inject(DatePipe);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroy$ = new Subject<void>();
  private readonly catalogTranslate = (key: string) => this.translate.instant(key);

  readonly processes = signal<ProcessDefinition[]>([]);
  readonly triggers = signal<ProcessEventTriggerResponse[]>([]);
  readonly events = signal<Array<{ key: string; displayName?: string; description?: string }>>([]);
  readonly subscriptions = signal<ProcessSubscriptionResponse[]>([]);
  readonly activityByKey = signal(indexProcessActivity([], iso => iso));
  readonly loading = signal(false);
  readonly errorMessage = signal('');
  readonly searchQuery = signal('');
  readonly catalogOrder = signal<ProcessCatalogOrder>('processes');
  readonly missingFailSubscriber = signal(false);
  readonly notificationRules = signal<NotificationRuleDto[]>([]);
  readonly catalog = computed(() => attachProcessActivity(
    buildProcessCatalog(
      this.processes(),
      this.triggers(),
      this.events(),
      this.subscriptions(),
    ),
    this.activityByKey(),
  ));
  readonly groups = computed(() => groupProcesses(
    this.catalog().standalone.filter(process =>
      standaloneProcessMatchesQuery(process, this.searchQuery(), this.catalogTranslate)
      && this.passesFailSubscriberFilter(process.key)),
  ));
  readonly flows = computed(() => this.catalog().flows.filter(flow =>
    processFlowMatchesQuery(flow, this.searchQuery(), this.catalogTranslate)
    && flow.nodes.some(node => this.passesFailSubscriberFilter(node.key))));
  readonly flowGroups = computed(() => groupProcessFlows(this.flows()));
  readonly moduleSections = computed(() => {
    const flowsById = new Map(this.flowGroups().map(group => [group.id, group.processes]));
    const cardsById = new Map(this.groups().map(group => [group.id, group.processes]));
    return PROCESS_GROUPS
      .map(group => {
        const flows = flowsById.get(group.id) ?? [];
        const processes = cardsById.get(group.id) ?? [];
        return {
          ...group,
          flows,
          processes,
          count: flows.length + processes.length,
        };
      })
      .filter(group => group.count > 0);
  });
  readonly hasCatalogFlows = computed(() => this.moduleSections().some(section => section.flows.length > 0));
  readonly eventCards = computed(() => buildEventCatalog(
    this.events() as ProcessEventCatalogItem[],
    this.triggers(),
    this.processes(),
  ).filter(card => eventCatalogCardMatchesQuery(card, this.searchQuery(), this.catalogTranslate)));
  readonly eventGroups = computed(() => groupEvents(this.eventCards()));
  readonly eventsFirst = computed(() => this.catalogOrder() === 'events');
  readonly emptyCatalogMessage = computed(() => {
    if (this.searchQuery().trim()) {
      return 'No catalog matches';
    }
    if (this.missingFailSubscriber()) {
      return 'No processes without a fail subscriber';
    }
    return 'No process definitions found';
  });
  readonly coreEvents = computed(() => coreProcessEvents(this.events()).filter(event =>
    catalogQueryMatches(this.searchQuery(), event.eventKey, event.eventLabel, this.catalogTranslate(event.eventLabel)),
  ));
  readonly catalogOrderChips = computed(() => ([
    { id: 'processes' as const, label: 'Processes first', selected: this.catalogOrder() === 'processes' },
    { id: 'events' as const, label: 'Events first', selected: this.catalogOrder() === 'events' },
  ]));
  readonly showEmptyState = computed(() => {
    if (this.loading()) {
      return false;
    }
    const noProcesses = !this.moduleSections().length;
    if (this.eventsFirst()) {
      return noProcesses && !this.eventGroups().length;
    }
    return noProcesses;
  });

  ngOnInit(): void {
    this.route.queryParamMap.pipe(
      map(params => readCatalogOrder(params.get('order'))),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(order => this.catalogOrder.set(order));

    this.route.queryParamMap.pipe(
      map(params => params.get('failSubscriber') === 'missing'),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(missing => this.missingFailSubscriber.set(missing));

    this.loading.set(true);
    forkJoin({
      processes: this.service.assignmentsProcessesGet(),
      triggers: this.triggersApi.processEventTriggersGet(),
      events: this.triggersApi.processEventTriggersEventsGet(),
      subscriptions: this.subscriptionsApi.processSubscriptionsGet(),
      activity: this.service.assignmentsProcessesActivityGet().pipe(catchError(() => of([]))),
      notificationRules: this.notificationsApi.getRules().pipe(catchError(() => of([] as NotificationRuleDto[]))),
    }).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.loading.set(false)),
    ).subscribe({
      next: ({ processes, triggers, events, subscriptions, activity, notificationRules }) => {
        this.processes.set(operatorCatalogProcesses(processes as ProcessDefinition[]));
        this.triggers.set(triggers);
        this.events.set(events as Array<{ key: string; displayName?: string; description?: string }>);
        this.subscriptions.set(subscriptions);
        this.notificationRules.set(notificationRules);
        this.applyActivity(activity);
      },
      error: () => this.errorMessage.set('Failed to load process definitions'),
    });

    interval(PROCESS_ACTIVITY_POLL_MS)
      .pipe(
        switchMap(() => this.service.assignmentsProcessesActivityGet().pipe(catchError(() => EMPTY))),
        takeUntil(this.destroy$),
      )
      .subscribe(activity => this.applyActivity(activity));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectCatalogOrder(order: ProcessCatalogOrder): void {
    this.catalogOrder.set(order);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { order: order === 'processes' ? null : order },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  toggleMissingFailSubscriber(): void {
    const missing = !this.missingFailSubscriber();
    this.missingFailSubscriber.set(missing);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { failSubscriber: missing ? 'missing' : null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  openAssignments(): void {
    void this.router.navigate(['/admin/orchestration/assignments']);
  }

  openSubscriptions(): void {
    void this.router.navigate(['/admin/orchestration/subscriptions']);
  }

  notifyPeople(eventKey: string): void {
    void this.router.navigate([NOTIFY_PEOPLE_PATH], {
      queryParams: notifyPeopleQueryParams({ eventKey }),
    });
  }

  private passesFailSubscriberFilter(processKey: string): boolean {
    return !this.missingFailSubscriber() || !processHasFailSubscriber(processKey, this.notificationRules());
  }

  private applyActivity(activity: ProcessAssignmentActivityResponse[] | null | undefined): void {
    this.activityByKey.set(indexProcessActivity(activity, iso => this.datePipe.transform(iso, 'short') ?? iso));
  }
}

function readCatalogOrder(value: string | null): ProcessCatalogOrder {
  return value === 'events' ? 'events' : 'processes';
}
